import React, { memo, useCallback, useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform, type View, StyleSheet } from 'react-native';

import {
  pointerEventsStyles,
  IngredientCardV2,
  IngredientListItem,
  useResponsive,
  useUniqueNativeID,
} from '@sg/garnish';
import { IngredientKind } from '@sg/graphql-schema';

import { INGREDIENT_CARD_IMAGE_WIDTH } from '../../constants';
import { type PortionOption } from '../../machine';
import { CustomizationPortionStepper } from '../CustomizationPortionStepper';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientItem = memo((props: Props) => {
  const {
    variation = 'card',
    id,
    kind,
    name,
    assetUrl,
    assetAspectRatioY,
    assetAspectRatioX,
    isOutOfStock,
    quantity,
    notice,
    upsellPrice,
    activeStepperId,
    isSelected,
    canAddMore,
    withMinHeight,
    isMutedStateEnabled,
    allowsOnlySinglePortion,
    isSplitPortion,
    size,
    dataSet,
    maxPortionsForGroup,
    portionOptions,
    isUpsellPricingEnabled,
    onIncrement,
    onDecrement,
    onRemove,
    onToggleSplitPortion,
    setActiveStepperId,
    resetStepperTimeout,
    clearStepperTimeout,
  } = props;

  const { formatMessage, formatNumber } = useIntl();
  const { match } = useResponsive();

  // ─── Refs ────────────────────────────────────────────────────────────

  const shouldUseTimeoutRef = useRef(false);
  const isKeyPressedRef = useRef(false); // Used to track if a key was pressed to activate the stepper
  const isHoveredRef = useRef(false);
  const cardRef = useRef<View>(null);

  // ─────────────────────────────────────────────────────────────────────

  const a11yLabel = formatMessage(messages.a11yCardLabel, {
    quantity,
    ingredient_name: name,
  });

  const a11yLabelSinglePortionAdd = formatMessage(messages.a11yLabelSinglePortionAdd, {
    ingredient_name: name,
  });
  const a11yLabelSinglePortionRemove = formatMessage(messages.a11yLabelSinglePortionRemove, {
    ingredient_name: name,
  });
  const a11yLabelSinglePortion =
    quantity > 0 ? a11yLabelSinglePortionRemove : a11yLabelSinglePortionAdd;

  const stepperNativeId = useUniqueNativeID();

  const hasSplitPortion = isSplitPortion && quantity !== maxPortionsForGroup;
  const splitPortionIconSize = match([15, 17]);

  // ─── Flags ───────────────────────────────────────────────────────────

  const isDressing = kind === IngredientKind.Dressing;
  const isStepperActive = activeStepperId === id;
  const canRenderNotice = Boolean(notice) && !isOutOfStock;
  const isNewIngredient = quantity === 0;
  const cannotAddMorePortions = isNewIngredient && !canAddMore;
  const isMuted = isMutedStateEnabled && (isOutOfStock || cannotAddMorePortions); // prettier-ignore
  const hasQuantityStepper = !(isDressing || allowsOnlySinglePortion);
  const hasPortionStepper = Boolean(isUpsellPricingEnabled && portionOptions?.length);
  const shouldRenderUpsellPrice =
    isUpsellPricingEnabled &&
    upsellPrice &&
    (quantity === 0 || (isStepperActive && !hasPortionStepper));

  // ─── Helpers ─────────────────────────────────────────────────────────

  const focusCard = useCallback(() => {
    if (Platform.OS !== 'web') return;

    try {
      cardRef.current?.focus?.();
    } catch {}
  }, []);

  const closeStepper = useCallback(() => {
    setActiveStepperId(null);
    isKeyPressedRef.current = false;

    if (variation === 'card') {
      focusCard();
    }
  }, [setActiveStepperId, focusCard, variation]);

  const scheduleStepperClose = useCallback(() => {
    if (isHoveredRef.current) {
      clearStepperTimeout();

      return;
    }

    resetStepperTimeout();
  }, [clearStepperTimeout, resetStepperTimeout]);

  const handleHoverIn = useCallback(() => {
    isHoveredRef.current = true;

    // Don't clear the timer if another card's stepper is the active one.
    if (activeStepperId !== null && !isStepperActive) return;

    clearStepperTimeout();
  }, [clearStepperTimeout, activeStepperId, isStepperActive]);

  const handleHoverOut = useCallback(() => {
    isHoveredRef.current = false;

    if (!isStepperActive) return;

    resetStepperTimeout();
  }, [isStepperActive, resetStepperTimeout]);

  const incrementPortion = useCallback(() => {
    onIncrement(id);

    if (shouldUseTimeoutRef.current) {
      scheduleStepperClose();
    }
  }, [id, onIncrement, scheduleStepperClose]);

  const decrementPortion = useCallback(() => {
    onDecrement(id);

    if (shouldUseTimeoutRef.current) {
      scheduleStepperClose();
    }
  }, [id, onDecrement, scheduleStepperClose]);

  const openStepper = useCallback(() => {
    setActiveStepperId(id);

    if (shouldUseTimeoutRef.current) {
      scheduleStepperClose();
    }
  }, [id, setActiveStepperId, scheduleStepperClose]);

  const handleRemove = useCallback(() => {
    if (isSplitPortion) {
      onToggleSplitPortion?.(id);

      return;
    }

    if (quantity === 1) {
      onDecrement(id);

      return;
    }

    onRemove?.(id);
  }, [id, onRemove, quantity, onDecrement, isSplitPortion, onToggleSplitPortion]);

  const handleSelectPortion = useCallback(
    (targetPortions: number) => {
      const delta = targetPortions - quantity;

      if (delta === 0) {
        closeStepper();

        return;
      }

      if (delta > 0) {
        Array.from({ length: delta }).forEach(() => onIncrement(id));
      }

      if (delta < 0) {
        Array.from({ length: Math.abs(delta) }).forEach(() => onDecrement(id));
      }

      scheduleStepperClose();
    },
    [id, quantity, onIncrement, onDecrement, scheduleStepperClose, closeStepper],
  );

  const handlePortionRemove = useCallback(() => {
    Array.from({ length: quantity }).forEach(() => onDecrement(id));

    closeStepper();
  }, [id, quantity, onDecrement, closeStepper]);

  const handlePress = useCallback(
    (withTimeout: boolean) => {
      shouldUseTimeoutRef.current = withTimeout;
      isKeyPressedRef.current = false;

      if (isSplitPortion) {
        onToggleSplitPortion?.(id);

        return;
      }

      const shouldAddPortion = isNewIngredient && (isDressing || allowsOnlySinglePortion); // prettier-ignore
      const shouldRemovePortion = !isNewIngredient && (isDressing || allowsOnlySinglePortion); // prettier-ignore

      if (isStepperActive) {
        closeStepper();

        return;
      }

      if (shouldAddPortion) {
        onIncrement(id);

        return;
      }

      if (shouldRemovePortion) {
        onDecrement(id);

        return;
      }

      if (isNewIngredient) {
        onIncrement(id);
      }

      if (cannotAddMorePortions || allowsOnlySinglePortion) {
        return;
      }

      openStepper();
    },
    [
      id,
      isNewIngredient,
      isDressing,
      allowsOnlySinglePortion,
      isStepperActive,
      cannotAddMorePortions,
      isSplitPortion,
      onToggleSplitPortion,
      openStepper,
      closeStepper,
      onIncrement,
      onDecrement,
    ],
  );

  const handleToggleSplitPortion = useCallback(() => {
    onToggleSplitPortion?.(id);
  }, [id, onToggleSplitPortion]);

  const onPress = useCallback(() => {
    const isKeyPressed = isKeyPressedRef.current;

    handlePress(!isKeyPressed);
  }, [handlePress]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!hasQuantityStepper) return;

      const isEscapeKey = event?.code === 'Escape';
      const isUnrelatedKey = event?.code === 'Tab';
      const isSameTarget = event?.target === cardRef.current;

      if (isEscapeKey) {
        closeStepper();

        return;
      }

      if (!isSameTarget || isUnrelatedKey) {
        return;
      }

      isKeyPressedRef.current = true;
    },
    [closeStepper, hasQuantityStepper],
  );

  // ─────────────────────────────────────────────────────────────────────

  const incrementLabel = formatMessage(messages.a11yIncrementLabel, {
    ingredient_name: name,
    quantity,
  });
  const decrementLabel = formatMessage(messages.a11yDecrementLabel, {
    ingredient_name: name,
    quantity,
  });

  const upsellPriceFormatted = upsellPrice
    ? formatNumber(upsellPrice / 100, { style: 'currency', currency: 'USD' })
    : 0;

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isStepperActive) return;

    return () => {
      isKeyPressedRef.current = false;
    };
  }, [isStepperActive]);

  // ─── Line Item Variation ─────────────────────────────────────────────

  if (variation === 'list-item') {
    const shouldRenderControls = !isOutOfStock;
    const shouldRenderAddRemoveToggle = shouldRenderControls && (isDressing || isSplitPortion || allowsOnlySinglePortion); // prettier-ignore
    const shouldRenderRemoveButton = shouldRenderControls && !shouldRenderAddRemoveToggle && quantity > 0 && onRemove; // prettier-ignore
    const shouldRenderQuantityStepper = shouldRenderControls && !shouldRenderAddRemoveToggle && !isSplitPortion; // prettier-ignore

    const addRemoveHandler = quantity > 0 ? handleRemove : incrementPortion;
    const addRemoveToggleHandler = isSplitPortion ? handleToggleSplitPortion : addRemoveHandler; // prettier-ignore

    const addRemoveToggleA11yLabel =
      quantity > 0
        ? formatMessage(messages.a11yLabelSinglePortionRemove, { ingredient_name: name }) // prettier-ignore
        : formatMessage(messages.a11yLabelSinglePortionAdd, { ingredient_name: name }); // prettier-ignore
    const addRemoveToggleLabel =
      quantity > 0 ? formatMessage(messages.removeLabel) : formatMessage(messages.addLabel);

    return (
      <IngredientListItem.Container isMuted={isMuted} dataSet={dataSet}>
        <IngredientListItem.Image
          url={assetUrl}
          alt={name}
          imageWidth={INGREDIENT_CARD_IMAGE_WIDTH}
          isSelected={isSelected}
        />

        <IngredientListItem.ContentContainer>
          <IngredientListItem.Title>{name}</IngredientListItem.Title>

          {canRenderNotice ? (
            <IngredientListItem.NoticeLabel>{notice}</IngredientListItem.NoticeLabel>
          ) : null}

          {isOutOfStock ? (
            <IngredientListItem.NoticeLabel>
              <FormattedMessage {...messages.unavailableLabel} />
            </IngredientListItem.NoticeLabel>
          ) : null}

          {isUpsellPricingEnabled && upsellPrice ? (
            <IngredientCardV2.UpsellBadge>
              <FormattedMessage
                {...messages.upsellPriceBadge}
                values={{ price: upsellPriceFormatted }}
              />
            </IngredientCardV2.UpsellBadge>
          ) : null}
        </IngredientListItem.ContentContainer>

        {shouldRenderAddRemoveToggle ? (
          <IngredientListItem.Button
            onPress={addRemoveToggleHandler}
            accessibilityLabel={addRemoveToggleA11yLabel}
          >
            {addRemoveToggleLabel}
          </IngredientListItem.Button>
        ) : null}

        {shouldRenderRemoveButton ? (
          <IngredientListItem.Button
            onPress={handleRemove}
            accessibilityLabel={formatMessage(messages.a11yLabelSinglePortionRemove, {
              ingredient_name: name,
            })}
          >
            <FormattedMessage {...messages.removeLabel} />
          </IngredientListItem.Button>
        ) : null}

        {shouldRenderQuantityStepper ? (
          <IngredientListItem.QuantityStepper
            onIncrease={incrementPortion}
            onDecrease={decrementPortion}
            isDisabled={false}
            quantity={quantity}
            increaseButtonAccessibilityLabel={incrementLabel}
            decreaseButtonAccessibilityLabel={decrementLabel}
          />
        ) : null}
      </IngredientListItem.Container>
    );
  }

  // ─── Card Variation ────────────────────────────────────────────────────

  return (
    <IngredientCardV2.Container
      isMuted={isMuted}
      isHoverDisabled={isStepperActive}
      dataSet={dataSet}
      onKeyDown={hasQuantityStepper ? onKeyDown : undefined}
      onHoverIn={hasQuantityStepper ? handleHoverIn : undefined}
      onHoverOut={hasQuantityStepper ? handleHoverOut : undefined}
    >
      <IngredientCardV2.Pressable
        ref={cardRef}
        accessibilityLabel={hasQuantityStepper ? a11yLabel : a11yLabelSinglePortion} // prettier-ignore
        disabled={isOutOfStock}
        onPress={onPress}
        aria-selected={quantity > 0}
        aria-expanded={hasQuantityStepper ? isStepperActive : undefined}
        aria-controls={hasQuantityStepper && isStepperActive ? stepperNativeId : undefined} // prettier-ignore
        isNestedElementFocused={isStepperActive}
      />

      <IngredientCardV2.ImageContainer
        style={[
          styles.imageContainer,
          isStepperActive ? pointerEventsStyles.auto : pointerEventsStyles.none,
        ]}
      >
        <IngredientCardV2.Image
          source={assetUrl}
          accessibilityLabel={name}
          imageWidth={INGREDIENT_CARD_IMAGE_WIDTH}
          aspectRatioY={assetAspectRatioY}
          aspectRatioX={assetAspectRatioX}
        />

        {isStepperActive && hasPortionStepper && portionOptions ? (
          <CustomizationPortionStepper
            options={portionOptions}
            ingredientName={name}
            currentPortions={quantity}
            onSelectPortion={handleSelectPortion}
            onRemove={handlePortionRemove}
          />
        ) : null}

        {isStepperActive && hasQuantityStepper && !hasPortionStepper ? (
          <IngredientCardV2.QuantityStepper.Container size={size} nativeID={stepperNativeId}>
            <IngredientCardV2.QuantityStepper.StepperButton
              variation="decrease"
              size={size}
              onPress={decrementPortion}
              accessibilityLabel={decrementLabel}
              ariaDisabled={quantity === 0}
            />

            <IngredientCardV2.QuantityStepper.Label quantity={quantity} />

            <IngredientCardV2.QuantityStepper.StepperButton
              variation="increase"
              size={size}
              onPress={incrementPortion}
              accessibilityLabel={incrementLabel}
              autoFocusWeb={isStepperActive}
            />
          </IngredientCardV2.QuantityStepper.Container>
        ) : null}

        {!isStepperActive && quantity > 0 ? (
          <IngredientCardV2.Quantity
            shouldUseIcon={!hasQuantityStepper || isSplitPortion || hasSplitPortion} // prettier-ignore
            iconName={hasSplitPortion ? 'IconFractionHalf' : undefined}
            iconSize={hasSplitPortion ? splitPortionIconSize : undefined}
            palette={isUpsellPricingEnabled ? 'neutral' : 'kale'}
            size={size}
          >
            {quantity}
          </IngredientCardV2.Quantity>
        ) : null}

        {shouldRenderUpsellPrice ? (
          <IngredientCardV2.UpsellBadge variant="floating">
            <FormattedMessage
              {...messages.upsellPriceBadge}
              values={{ price: upsellPriceFormatted }}
            />
          </IngredientCardV2.UpsellBadge>
        ) : null}
      </IngredientCardV2.ImageContainer>

      <IngredientCardV2.LabelContainer withMinHeight={withMinHeight}>
        <IngredientCardV2.Label>{name}</IngredientCardV2.Label>

        {isOutOfStock ? (
          <IngredientCardV2.NoticeLabel>
            <FormattedMessage {...messages.unavailableLabel} />
          </IngredientCardV2.NoticeLabel>
        ) : null}

        {canRenderNotice ? (
          <IngredientCardV2.NoticeLabel>{notice}</IngredientCardV2.NoticeLabel>
        ) : null}
      </IngredientCardV2.LabelContainer>
    </IngredientCardV2.Container>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yCardLabel: {
    defaultMessage: `{quantity, select,
      0 {Add {ingredient_name}}
      other {Increase quantity of {ingredient_name} (current quantity: {quantity})}
    }`,
    description: 'Customization | Ingredient card | a11y label',
  },
  a11yLabelSinglePortionAdd: {
    defaultMessage: 'Add {ingredient_name}',
    description: 'Customization | Ingredient card | a11y label (add)',
  },
  a11yLabelSinglePortionRemove: {
    defaultMessage: 'Remove {ingredient_name}',
    description: 'Customization | Ingredient card | a11y label (remove)',
  },
  a11yIncrementLabel: {
    defaultMessage: 'Increase quantity of {ingredient_name} (current quantity: {quantity})',
    description: 'Customization | Ingredient card increment button | a11y label',
  },
  a11yDecrementLabel: {
    defaultMessage: 'Decrease quantity of {ingredient_name} (current quantity: {quantity})',
    description: 'Customization | Ingredient card decrement button | a11y label',
  },
  upsellPriceBadge: {
    defaultMessage: '+ {price}',
    description: 'Customization | Ingredient card | Upsell price badge',
  },
  unavailableLabel: {
    defaultMessage: 'Unavailable',
    description: 'Customization | Ingredient card | Unavailable label',
  },
  addLabel: {
    defaultMessage: 'Add',
    description: 'Customization | Ingredient card | Add label',
  },
  removeLabel: {
    defaultMessage: 'Remove',
    description: 'Customization | Ingredient card | Remove label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  imageContainer: {
    zIndex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  id: string;
  kind: IngredientKind | null;
  name: string;
  assetUrl: string;
  assetAspectRatioY?: number;
  assetAspectRatioX?: number;
  isOutOfStock: boolean;
  isSelected?: boolean;
  quantity: number;
  notice: string | undefined;
  upsellPrice?: number;
  isUpsellPricingEnabled?: boolean;
  activeStepperId: string | null;
  canAddMore: boolean;
  allowsOnlySinglePortion: boolean;
  withMinHeight?: boolean;
  variation?: 'card' | 'list-item';
  size?: 'sm' | 'md';
  dataSet?: Record<string, string | number>;
  maxPortionsForGroup: number;
  portionOptions?: PortionOption[];

  // A feature flag to enable the muted state for the ingredient card for states like "out of stock" or "cannot add more portions"
  isMutedStateEnabled?: boolean;
  isSplitPortion?: boolean;

  onIncrement: (ingredientId: string) => void;
  onDecrement: (ingredientId: string) => void;
  onRemove?: (ingredientId: string) => void;
  onToggleSplitPortion?: (ingredientId: string) => void;
  setActiveStepperId: (id: string | null) => void;
  resetStepperTimeout: () => void;
  clearStepperTimeout: () => void;
};
